import {
  createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react'
import { AuthClient, type Session } from './session.js'
import { readSignUpFromHash } from './signup.js'

export interface AuthState {
  client: AuthClient
  session: Session | null
  isLive: boolean
  accessToken: () => Promise<string | null>
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, redirectTo?: string) => Promise<{ confirmationRequired: boolean }>
  signOut: () => Promise<void>
  switchAccount: (userId: string) => Promise<void>
  addAccount: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

/** Canonical auth state, refusing when no explicit provider owns the session. */
export function useInjectedAuth(): AuthState {
  const state = useContext(AuthContext)
  if (!state) throw new Error('authentication requires an AuthProvider')
  return state
}

export interface InjectedAuthProviderProps {
  children: ReactNode
  client: AuthClient
  live: boolean
}

/** Provider core with no environment singleton or import-time session access. */
export function InjectedAuthProvider({ children, client, live }: InjectedAuthProviderProps) {
  const pinnedClient = useRef(client).current
  if (client !== pinnedClient) {
    throw new Error('AuthProvider client cannot change after mount; remount the authentication boundary')
  }

  const [session, setSession] = useState<Session | null>(pinnedClient.current)
  const [signUpCallback] = useState(() => readSignUpFromHash())
  const acceptedSignUpGrant = useRef(false)
  useEffect(() => {
    setSession(pinnedClient.current)
    const unsubscribe = pinnedClient.subscribe(setSession)
    if (signUpCallback.grant && !acceptedSignUpGrant.current) {
      acceptedSignUpGrant.current = true
      void pinnedClient.acceptSignUpGrant(signUpCallback.grant)
    }
    return unsubscribe
  }, [pinnedClient, signUpCallback])

  const value = useMemo<AuthState>(() => ({
    client: pinnedClient,
    session,
    isLive: live,
    accessToken: async () => {
      if (!live || !pinnedClient.current) return null
      try { return await pinnedClient.getAccessToken() } catch { return null }
    },
    signIn: async (email, password) => { await pinnedClient.signIn(email, password) },
    signUp: async (email, password, redirectTo) => {
      const result = await pinnedClient.signUp(email, password, redirectTo)
      return { confirmationRequired: result.confirmationRequired }
    },
    signOut: async () => { await pinnedClient.signOut() },
    switchAccount: async (userId) => { await pinnedClient.switchAccount(userId) },
    addAccount: async () => { await pinnedClient.addAccount() },
  }), [pinnedClient, live, session])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
